/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DFF.FLAGS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DffFlagsPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail) over the
 * SAME data/logic layer: reads via apiDb (dff_flag), and the enable/disable
 * write routes through the Common Routine (routedUpdate) — §2 single write path,
 * mirroring the desktop `is_active` toggle. The desktop page's targeting-rule /
 * endpoint-guard editors stay on desktop; the mobile screen focuses on the flag
 * list + enable/disable, the primary on-the-go action. §16: never the desktop
 * grid re-hosted in a phone viewport.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, Flag } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips,
  MobileStatusPill,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DFF.FLAGS';

interface MobileFlagRow {
  dff_flag_id: number;
  internal_code: string;
  name: string;
  value_type: string;
  default_value: unknown;
  is_active: 'Y' | 'N';
}

type StatusFacet = 'all' | 'enabled' | 'disabled';

interface GuardLite {
  dff_endpoint_guard_id: number;
  flag_code: string;
  match_type: string;
  match_value: string;
  action: string;
  reroute_to: string | null;
}

export function MobileDffFlagsPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  // Flag labels are the (untranslated) `name` column; the active locale still
  // drives the localized CHECK-enum value_type labels (§17.B11), so re-read on
  // locale change to keep those in step.
  const activeLocale = useActiveLocale();
  const [flags, setFlags] = useState<MobileFlagRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [typeFacet, setTypeFacet] = useState<string>('all');
  const [selected, setSelected] = useState<MobileFlagRow | null>(null);
  // §17.B13 on mobile: the detail sheet is where a row reveals its related
  // records. Mirrors the desktop page's Endpoint Guards group.
  const [guards, setGuards] = useState<GuardLite[]>([]);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: fErr } = await apiDb
        .from('dff_flag')
        .select('dff_flag_id, internal_code, name, value_type, default_value, is_active')
        .order('internal_code');
      if (fErr) throw fErr;
      setFlags((data ?? []) as MobileFlagRow[]);

      const { data: gData } = await apiDb
        .from('dff_endpoint_guard')
        .select('dff_endpoint_guard_id, flag_code, match_type, match_value, action, reroute_to')
        .order('flag_code');
      setGuards((gData ?? []) as GuardLite[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (enum labels follow it).
  useEffect(() => { void reload(); }, [activeLocale]);

  // §17.B11: the CHECK-enum value_type never renders as its raw literal — it goes
  // through i18n with the stored code as the fallback default.
  const typeLabel = (vt: string) => t(`mobile.flags.value_type.${vt}`, vt);

  const defaultText = (row: MobileFlagRow): string => {
    try { return JSON.stringify(row.default_value); }
    catch { return String(row.default_value); }
  };

  // Distinct value types present, for the type facet chips.
  const typeOptions = useMemo(
    () => [...new Set(flags.map((f) => f.value_type).filter(Boolean))].sort(),
    [flags],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return flags.filter((f) => {
      if (statusFacet === 'enabled' && f.is_active !== 'Y') return false;
      if (statusFacet === 'disabled' && f.is_active !== 'N') return false;
      if (typeFacet !== 'all' && f.value_type !== typeFacet) return false;
      if (!q) return true;
      return (
        f.internal_code.toLowerCase().includes(q) ||
        (f.name ?? '').toLowerCase().includes(q)
      );
    });
  }, [flags, filter, statusFacet, typeFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.flags.all', 'All') },
    { key: 'enabled', label: t('mobile.flags.enabled', 'Enabled') },
    { key: 'disabled', label: t('mobile.flags.disabled', 'Disabled') },
  ];

  const statusPill = (isActive: 'Y' | 'N') =>
    isActive === 'Y' ? t('mobile.flags.enabled', 'Enabled') : t('mobile.flags.disabled', 'Disabled');

  const toggleEnabled = async (f: MobileFlagRow) => {
    setActionBusy(true);
    setError(null);
    const next = f.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dff_flag', 'dff_flag_id', f.dff_flag_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.flags.title', 'Feature Flags')}
        description={t('mobile.flags.description', 'Backend feature flags and their enabled state.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.flags.search', 'Search flags')}
          aria-label={t('mobile.flags.search', 'Search flags')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {typeOptions.length > 0 && (
        <MobileFilterChips
          chips={[
            { value: 'all', label: t('mobile.flags.all_types', 'All types') },
            ...typeOptions.map((vt) => ({ value: vt, label: typeLabel(vt) })),
          ]}
          isActive={(v) => typeFacet === v}
          onSelect={(v) => setTypeFacet(v)}
        />
      )}

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.flags.none', 'No feature flags match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((f) => (
            <MobileCardRow
              key={f.dff_flag_id}
              lead={<Flag />}
              leadTone={f.is_active === 'Y' ? 'blue' : 'slate'}
              title={f.name || f.internal_code}
              subtitle={`${f.internal_code} · ${typeLabel(f.value_type)}`}
              trailing={<MobileStatusPill tone={f.is_active === 'Y' ? 'ok' : 'no'}>{statusPill(f.is_active)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(f)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.flags.detail', 'Flag detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Flag size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.flags.status', 'Status')}
                value={<MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>{statusPill(selected.is_active)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.flags.type', 'Type')} value={typeLabel(selected.value_type)} />
              <MobileDetailRow
                label={t('mobile.flags.default', 'Default')}
                value={<span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{defaultText(selected)}</span>}
              />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block
                (MobileRelatedChildren lays out vertically; side-by-side cards are
                the desktop panel's layout and are too narrow here). */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'guards',
                    table: 'dff_endpoint_guard',
                    pkColumn: 'dff_endpoint_guard_id',
                    title: t('mobile.flags.related.guards', 'Endpoint Guards'),
                    items: guards
                      .filter(g => g.flag_code === selected.internal_code)
                      .map(g => ({
                        id: g.dff_endpoint_guard_id,
                        label: `${g.match_type}: ${g.match_value || '—'}`,
                        sub: g.action + (g.reroute_to ? ` → ${g.reroute_to}` : ''),
                      })),
                  },
                ]}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={() => void toggleEnabled(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('mobile.flags.disable', 'Disable') : t('mobile.flags.enable', 'Enable')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
